/**
 * Footer Component
 * 
 * Site-wide footer with company branding and copyright information.
 * 
 * Features:
 * - Company name and tagline
 * - Copyright notice with dynamic year
 * - Responsive layout (stacked on mobile, side-by-side on desktop)
 * 
 * @component
 * @example
 * ```tsx
 * <Footer />
 * ```
 */

import { memo } from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { getEmail, getPhone } from "@/utils/emailProtection";

const Footer = () => {
  return (
    <footer className="bg-background border-t border-border" role="contentinfo">
      <div className="container mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
          {/* Logo and Contact - Takes more space */}
          <div className="md:col-span-5 flex flex-col items-start">
            <Link 
              to="/" 
              className="hover:opacity-80 transition-opacity mb-6"
              aria-label="OceanNova home"
            >
              <img 
                src="/assets/oceannova-vertical-logo.png" 
                alt="OceanNova - Different Perspective" 
                className="h-32 w-auto"
                width="200"
                height="128"
                loading="lazy"
              />
            </Link>
            <div className="pl-1">
              <div className="mb-6 max-w-sm h-6">
                {/* Spacing placeholder - tagline removed */}
              </div>
              <address className="space-y-3 not-italic">
                <a 
                  href={`mailto:${getEmail()}`}
                  className="block hover:text-primary transition-colors text-muted-foreground"
                  aria-label={`Email OceanNova at ${getEmail()}`}
                >
                  {getEmail()}
                </a>
                <a 
                  href={`tel:${getPhone()}`}
                  className="block hover:text-primary transition-colors text-muted-foreground"
                  aria-label={`Call OceanNova at ${getPhone()}`}
                >
                  {getPhone()}
                </a>
                <a 
                  href="https://www.linkedin.com/company/oceannova"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-primary transition-colors text-muted-foreground"
                  aria-label="Follow OceanNova on LinkedIn"
                >
                  <Linkedin className="h-5 w-5" aria-hidden="true" />
                  <span>LinkedIn</span>
                </a>
              </address>
            </div>
          </div>

          {/* Services Column */}
          <div className="md:col-span-4">
            <h3 className="font-semibold text-foreground text-lg mb-4">Services</h3>
            <nav className="space-y-3">
              <Link 
                to="/services/port-ship-agency" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Port & Ship Agency
              </Link>
              <Link 
                to="/services/ship-chartering" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Ship Chartering
              </Link>
              <Link 
                to="/services/project-cargo" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Project Cargo & Consulting
              </Link>
              <Link 
                to="/services/rail-road-logistics" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Rail & Road Logistics
              </Link>
              <Link 
                to="/services/terminal-port-development" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Terminal & Port Development
              </Link>
              <Link 
                to="/services/air-freight" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Air Freight
              </Link>
            </nav>
          </div>

          {/* Company Column */}
          <div className="md:col-span-3">
            <h3 className="font-semibold text-foreground text-lg mb-4">Company</h3>
            <nav className="space-y-3">
              <Link 
                to="/our-story" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Our Story
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-primary transition-colors text-muted-foreground"
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <p className="text-center text-muted-foreground text-sm">
            &copy; {new Date().getFullYear()} OceanNova. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);
